import React, { useState } from 'react';
import styled from '@emotion/styled';
import CustomCalendar from '../ui/Calendar';
import TestDriveDuration from './TestDriveDuration';
import TimeSlot from './TimeSlot';
import DotsLoading from '../ui/DotsLoading';

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #151414;
  border-radius: 20px;
  box-shadow: 0px 1px 15px rgba(69, 65, 78, 0.08);
  width: 900px;
  min-height: 700px;
  margin: 0 auto;
  overflow: hidden;
  
  @media (max-width: 1199px) {
      width: 100%;
      display: block;
      border-radius: 10px;
  }
`;

const Left = styled.div`
  flex: 2;
  padding: 40px 40px 40px 40px;
  background: #151414;
  display: flex;
  flex-direction: column;
  @media (max-width: 767px) {
    padding: 0;
  }
`;

const Right = styled.div`
  flex: 1;
  background: #363636;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  @media (max-width: 767px) {
    padding: 15px;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  }
`;

const Title = styled.h2`
  color: #EDEDED;
  font-size: 30px;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 600;
  margin-bottom: 32px;
    @media (max-width: 767px) {
     font-size: 18px;
      margin-bottom: 10px;
  }
`;

const SectionLabel = styled.div`
  color: #EDEDED;
  font-size: 18px;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 500;
  margin-bottom: 16px;
`;

const CalendarSection = styled.div`
  margin-bottom: 32px;
`;

const DurationSection = styled.div`
  margin-top: 48px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 20px;
  
  @media (max-width: 767px) {
     margin-bottom: 20px;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  height: 48px;
  background: #F43F60;
  border-radius: 14px;
  border: none;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #ededed;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #d92b4c;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  height: 48px;
  background: #232323;
  border-radius: 14px;
  border: none;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #ededed;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #363636;
  }
`;

const TimeLabel = styled.div`
  color: #EDEDED;
  font-size: 18px;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 500;
  margin-bottom: 16px;
`;

const SubLabel = styled.div`
  color: #EDEDED;
  font-size: 16px;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 500;
  margin-bottom: 24px;
`;





function AppointmentBooking({
    isSubmitting = false,
    onSubmit = () => { },
    onCancel = () => { },
    availableSlots = [],
    availableSlotsLoading = false,
    availableSlotsError = null,
    setSelectedDate = () => { },
    selectedDate = new Date(),
    onClearError = () => { },
}) {
    const [selectedSlotIdx, setSelectedSlotIdx] = useState(0);
    const handleSelectSlot = (idx) => {
        setSelectedSlotIdx(idx);
        onClearError(); // Clear any previous errors when selecting a new slot
    }

    const getDuration = (start, end) => {
        const startHour = parseInt(start.split(':')[0]);
        const startMinute = parseInt(start.split(':')[1]);
        const endHour = parseInt(end.split(':')[0]);
        const endMinute = parseInt(end.split(':')[1]);
        return (endHour - startHour) * 60 + (endMinute - startMinute);
    }
    const duration = selectedSlotIdx ? getDuration(availableSlots[selectedSlotIdx].start, availableSlots[selectedSlotIdx].end) : null;

    const handleSubmit = () => {
        onSubmit({
            date: selectedDate,
            timeSlot: availableSlots[selectedSlotIdx]
        })
    }
    return (
        <Wrapper>
            <Left>
                <Title>Schedule for a test drive</Title>
                <SectionLabel>Select a date</SectionLabel>
                <CalendarSection>
                    <CustomCalendar value={selectedDate} onChange={setSelectedDate} />
                </CalendarSection>
                {duration && <DurationSection>
                    <TestDriveDuration value={duration} />
                </DurationSection>}
                <ButtonRow>
                    <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ?
                            <DotsLoading dots={3} color="#EDEDED" style={{ margin: 'auto' }} />
                            :
                            'Create'}
                    </PrimaryButton>
                </ButtonRow>
            </Left>
            <Right>
                <TimeLabel>Select Time</TimeLabel>
                {availableSlotsLoading ? <DotsLoading dots={3} /> : availableSlotsError ? <div>Error: {typeof availableSlotsError === 'string' ? availableSlotsError : availableSlotsError?.message || 'An error occurred'}</div> : availableSlots?.length ? <>
                    <SubLabel>Time you’re available</SubLabel>
                    {availableSlots.map((slot, idx) => (
                        <TimeSlot
                            key={slot.start}
                            start={slot.start}
                            end={slot.end}
                            isSelected={selectedSlotIdx === idx}
                            onSelect={() => handleSelectSlot(idx)}
                        />
                    ))}
                </>
                    : <SubLabel>No slots available for this date</SubLabel>}
            </Right>
        </Wrapper>
    );
}

export default AppointmentBooking;  